/**
 * AiCloud FAQ Sayfasi
 * Route: /aicloud/app/faq
 */

import { ChevronDown, ChevronRight, ExternalLink, HelpCircle, Terminal } from 'lucide-react'
import { useState } from 'react'

interface FAQItem {
  q: string
  a: string
  cli?: string
  link?: { label: string; url: string }
}

interface FAQSection {
  title: string
  icon: string
  items: FAQItem[]
}

const FAQ_SECTIONS: FAQSection[] = [
  {
    title: 'Cloudflare Token',
    icon: '☁️',
    items: [
      {
        q: 'How do I get a Cloudflare API Token?',
        a: '1. Go to https://dash.cloudflare.com/profile/api-tokens\n2. Click "Create Token"\n3. Select "Custom Token"\n4. Add permissions: Account > Workers AI (Edit), Account > R2 Storage (Edit)\n5. Copy the token and paste it in the Connect form.',
        cli: 'aicloud init --provider cloudflare',
        link: { label: 'Cloudflare API Tokens', url: 'https://dash.cloudflare.com/profile/api-tokens' }
      },
      {
        q: 'Where do I find my Cloudflare Account ID?',
        a: 'Your Account ID is visible on the right side of the Cloudflare dashboard homepage (dash.cloudflare.com). It is a 32-character hex string.',
        link: { label: 'Cloudflare Dashboard', url: 'https://dash.cloudflare.com' }
      },
      {
        q: 'What permissions does the token need?',
        a: 'Minimum required:\n- Account > Workers AI: Edit\n- Account > Workers Scripts: Edit\n- Account > D1: Edit\n- Account > R2 Storage: Edit\n- Account > KV Storage: Edit\n\nFor read-only explorer access, use Read instead of Edit.',
      },
    ]
  },
  {
    title: 'Azure Credentials',
    icon: '🔷',
    items: [
      {
        q: 'How do I create Azure credentials?',
        a: '1. Azure Portal → Azure Active Directory → App registrations\n2. Click "New registration", name it "aicloud-app"\n3. Note the Application (client) ID and Directory (tenant) ID\n4. Under "Certificates & secrets" → New client secret → copy the value\n5. Go to Subscriptions → your subscription → Access control (IAM)\n6. Add role assignment: Contributor → your app registration',
        cli: 'aicloud init --provider azure',
        link: { label: 'Azure App Registrations', url: 'https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps' }
      },
      {
        q: 'What is the Subscription ID?',
        a: 'Go to Azure Portal → Subscriptions. Your Subscription ID is listed there as a UUID (e.g. 12345678-1234-1234-1234-123456789012).',
      },
    ]
  },
  {
    title: 'CLI vs Dashboard',
    icon: '⌨️',
    items: [
      {
        q: 'What is the difference between CLI and Dashboard?',
        a: 'Both do the same thing — they connect your cloud providers and manage resources.\n\nDashboard: Visual interface, easier for beginners, real-time explorer.\nCLI: Faster for power users, scriptable, works in CI/CD pipelines.\n\nYou can use both — they share the same backend.',
      },
      {
        q: 'How do I install the AiCloud CLI?',
        a: 'Install via pip:\n  pip install aicloud-cli\n\nThen initialize:\n  aicloud init\n\nOr download the binary from the CLI Download page.',
        cli: 'pip install aicloud-cli && aicloud init',
      },
      {
        q: 'How do I list my cloud resources from CLI?',
        a: 'After connecting a provider:\n  aicloud explore cloudflare          # list accounts\n  aicloud explore cloudflare 1        # list resources\n  aicloud explore cloudflare 1 workers # list workers\n  aicloud explore aws 2 s3            # list S3 buckets',
        cli: 'aicloud explore cloudflare',
      },
    ]
  },
  {
    title: 'Security',
    icon: '🔒',
    items: [
      {
        q: 'How are my credentials stored?',
        a: 'All credentials are encrypted with AES-256 (Fernet) before being stored in the database. The encryption key is stored separately. Plain text credentials are never stored or logged.',
      },
      {
        q: 'Can RunAI access my cloud resources?',
        a: 'RunAI only accesses your resources when you explicitly trigger an action (sync, explore, deploy). We do not run background jobs on your cloud accounts without your knowledge.',
      },
      {
        q: 'How do I rotate my API token?',
        a: '1. Create a new token in your cloud provider dashboard\n2. Disconnect the old account in AiCloud\n3. Reconnect with the new token\n\nWe recommend rotating tokens every 90 days.',
      },
    ]
  },
  {
    title: 'Local LLM',
    icon: '💻',
    items: [
      {
        q: 'How do I connect a local LLM?',
        a: '1. Start LM Studio or Ollama on your machine\n2. Note the endpoint (default: http://localhost:1234 for LM Studio, http://localhost:11434 for Ollama)\n3. Go to AiCloud → API Keys → Local LLM tab\n4. Enter the endpoint URL and save\n\nAgents will automatically use your local LLM when available.',
        cli: 'aicloud keys local-llm connect --url http://localhost:1234',
      },
      {
        q: 'Which local LLM servers are supported?',
        a: 'Any OpenAI-compatible endpoint:\n- LM Studio (http://localhost:1234)\n- Ollama (http://localhost:11434)\n- llama.cpp server\n- vLLM\n- LocalAI\n\nThe endpoint must expose /v1/chat/completions.',
      },
    ]
  },
  {
    title: 'Petals P2P',
    icon: '🌐',
    items: [
      {
        q: 'What is Petals P2P?',
        a: 'Petals is a distributed inference network. Large models (70B+) are split across multiple community GPUs. You contribute part of your GPU and in return get access to large models at very low cost.\n\nYou earn FLZ tokens for each inference your GPU serves.',
        cli: 'aicloud keys petals connect',
        link: { label: 'Petals Documentation', url: 'https://petals.dev' }
      },
      {
        q: 'How much GPU do I need to contribute?',
        a: 'Minimum: 4GB VRAM (serves 1-2 transformer layers of a 70B model).\nRecommended: 8GB+ VRAM for better earnings.\n\nYou can set the contribution percentage in Settings.',
      },
    ]
  },
]

export default function FAQPage() {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({ 'Cloudflare Token': true })
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({})

  const toggleSection = (title: string) => {
    setOpenSections(prev => ({ ...prev, [title]: !prev[title] }))
  }

  const toggleItem = (key: string) => {
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }))
  }

  return (
    <div style={{ maxWidth: '760px' }}>
      {/* Baslik */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
        <HelpCircle size={22} color="#3b82f6" />
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>Help & FAQ</h1>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '2px 0 0' }}>
            Common questions about AiCloud setup, credentials, and CLI usage
          </p>
        </div>
      </div>

      {/* Sections */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {FAQ_SECTIONS.map(section => {
          const isOpen = openSections[section.title] ?? false
          return (
            <div key={section.title} style={{
              background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', overflow: 'hidden'
            }}>
              {/* Section header */}
              <button
                onClick={() => toggleSection(section.title)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '14px 16px', background: 'transparent', border: 'none',
                  cursor: 'pointer', color: 'var(--text-primary)', textAlign: 'left'
                }}
              >
                <span style={{ fontSize: '16px' }}>{section.icon}</span>
                <span style={{ flex: 1, fontSize: '14px', fontWeight: 600 }}>{section.title}</span>
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)', marginRight: '8px' }}>
                  {section.items.length} questions
                </span>
                {isOpen ? <ChevronDown size={15} color="var(--text-secondary)" /> : <ChevronRight size={15} color="var(--text-secondary)" />}
              </button>

              {/* Items */}
              {isOpen && (
                <div style={{ borderTop: '1px solid var(--border-color)' }}>
                  {section.items.map((item, idx) => {
                    const key = `${section.title}-${idx}`
                    const itemOpen = openItems[key] ?? false
                    return (
                      <div key={key} style={{ borderBottom: idx < section.items.length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                        {/* Question */}
                        <button
                          onClick={() => toggleItem(key)}
                          style={{
                            width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                            padding: '12px 16px 12px 20px', background: 'transparent', border: 'none',
                            cursor: 'pointer', color: 'var(--text-primary)', textAlign: 'left'
                          }}
                        >
                          <span style={{ flex: 1, fontSize: '13px', fontWeight: itemOpen ? 600 : 400 }}>{item.q}</span>
                          {itemOpen ? <ChevronDown size={13} color="var(--text-secondary)" /> : <ChevronRight size={13} color="var(--text-secondary)" />}
                        </button>

                        {/* Answer */}
                        {itemOpen && (
                          <div style={{ padding: '0 20px 16px 20px' }}>
                            <pre style={{
                              fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.7,
                              whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: '0 0 10px',
                              fontFamily: 'inherit'
                            }}>
                              {item.a}
                            </pre>

                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              {item.cli && (
                                <div style={{
                                  display: 'flex', alignItems: 'center', gap: '6px',
                                  padding: '6px 10px', background: 'var(--body-bg)',
                                  border: '1px solid var(--border-color)', borderRadius: '6px',
                                  fontSize: '12px', fontFamily: 'monospace', color: '#10b981'
                                }}>
                                  <Terminal size={12} />
                                  {item.cli}
                                </div>
                              )}
                              {item.link && (
                                <a
                                  href={item.link.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: '5px',
                                    padding: '6px 10px', background: 'rgba(59,130,246,0.08)',
                                    border: '1px solid rgba(59,130,246,0.2)', borderRadius: '6px',
                                    fontSize: '12px', color: '#3b82f6', textDecoration: 'none'
                                  }}
                                >
                                  <ExternalLink size={11} />
                                  {item.link.label}
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Alt - destek linki */}
      <div style={{
        marginTop: '24px', padding: '16px', background: 'var(--card-bg)',
        border: '1px solid var(--border-color)', borderRadius: '10px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '2px' }}>Still need help?</div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            Use the AI assistant (bottom right) or open a support ticket.
          </div>
        </div>
        <a href="/support" style={{
          padding: '8px 14px', background: 'var(--primary)', color: 'white',
          border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px',
          fontWeight: 600, textDecoration: 'none'
        }}>
          Open Support Ticket
        </a>
      </div>
    </div>
  )
}
